/**
 * Wire shape of the FieldDefinition DTO returned by
 * `GET /sysconfig/.../assignments/condition_fields`.
 *
 * The DTO + FieldType + EnumOption are imported from the generated openapi types so
 * the FE shape stays in sync with the BE Pydantic schemas. Local types here are the
 * FE-only contracts (hierarchy hints, hierarchical value shape) that the BE ships
 * inside a generic `hints` dict.
 */

import type { MatchConfig, RuleType } from 'react-querybuilder';
import { isEmpty, isNil } from 'lodash';

import type {
  FieldDefinitionListEnumOption,
  FieldDefinitionListFieldDefinition,
} from '~/server_shared/openapi/data-contracts';
import { FieldDefinitionListFieldType } from '~/server_shared/openapi/data-contracts';

import type { DatePrecision } from './operators';

// Re-export the generated BE enum as `FIELD_TYPE` so the FE has a single source of
// truth for FieldType values (the pydantic enum on the BE → openapi.json → here).
// PascalCase keys come from the codegen (Boolean, UsStateSelect, …); values are the
// snake_case strings the BE sends.
export { FieldDefinitionListFieldType as FIELD_TYPE };

// Use the string-literal projection so callers can also write `'select'` directly
// (test fixtures, JSON deserialised payloads, etc.).
export type FieldType = `${FieldDefinitionListFieldType}`;

export const DEFAULT_FIELD_TYPE: FieldType = FieldDefinitionListFieldType.String;

export type EnumOption = FieldDefinitionListEnumOption;

/** `hints` key marking a field republished under a type guard — an involved-party
 *  variant field, or a communication field copied into a channel group — whose
 *  catalog path is therefore synthetic: the runtime data lives at the variant's own
 *  `data_path`, behind a type guard the BE compiler injects. Offset values are not
 *  offered on these: the compiler has nowhere to put that guard inside a temporal
 *  call, so it rejects them. Must match
 *  `rules_engine/schemas/field_schemas.py::VARIANT_HINT_KEY`. */
export const VARIANT_HINT_KEY = 'variant';

/** `hints` key bounding a `number` field's admissible values, both ends inclusive. Declared by the
 *  provider that owns the field, because the bound belongs to the datum and not to the widget —
 *  `ai.confidence_score` is a fraction, so `>= 54` is a condition no agent call can fail. Absent on
 *  an unbounded number field, which keeps its free input. Must match
 *  `rules_engine/schemas/field_schemas.py::RANGE_HINT_KEY`. */
export const RANGE_HINT_KEY = 'range';

export interface NumericRangeHints {
  min: number;
  max: number;
}

/** `hints` key describing a `collection` field's elements. The block is
 *  `{rows?, elements?: [{name, label, field_type, enum_values?, group?, hints?}, …], element_root?}`.
 *
 *  Unlike the two keys above it is not a rewrite instruction — it is *vocabulary*. A collection
 *  field's runtime value is a list of maps, so a leaf on it quantifies rather than compares, and
 *  the nested group of predicates it carries is written in the element names this block declares.
 *  An element is not a published path (there is no context member at
 *  `claim.computed.communications.direction`, there is one per *row*), so it cannot be a field
 *  definition of its own. The block lists them, or — where each row is another context re-rooted
 *  onto it and the catalog carries that context's own fields — names the context in
 *  `element_root` and lists none; `collectionElements` reads either. `rows` says what one row is.
 *  Must match `rules_engine/schemas/field_schemas.py::COLLECTION_HINT_KEY`. */
export const COLLECTION_HINT_KEY = 'collection';

/** A collection whose rows are records — one per party on `claim.involved_parties` — so a count of
 *  them is a number an author can read, and the count modes are offered on it. Must match
 *  `rules_engine/schemas/field_schemas.py::COLLECTION_ROWS_RECORDS`. */
export const COLLECTION_ROWS_RECORDS = 'records';

/** A collection whose rows are the distinct tuples its provider grouped before publishing, where
 *  a count would count tuples rather than records, so the compiler refuses one. What a block that
 *  does not say is read as. Must match
 *  `rules_engine/schemas/field_schemas.py::COLLECTION_ROWS_GROUPED`. */
export const COLLECTION_ROWS_GROUPED = 'grouped_tuples';

/** One attribute of the rows a collection field holds.
 *
 *  `name` is both the key path the runtime element carries and the *relative* spelling a stored
 *  sub-leaf's `field` uses: bare (`direction`) in a listed block, dotted
 *  (`variant.involved_vehicle.is_total_loss`) for a re-rooted context. `enum_values` is present
 *  only where the element has an operand space — a `datetime` element declares none, and its
 *  operand is a date or an offset whose anchor space is the catalog rather than this block.
 *  `group` files it in the body's field picker, and `hints` are its own — a type guard, a range, a
 *  hierarchy — read the way a catalog field's are. */
export interface CollectionElement {
  name: string;
  label?: string;
  field_type: FieldType;
  enum_values?: EnumOption[] | null;
  group?: string[];
  hints?: Record<string, unknown>;
}

export interface CollectionHints {
  rows?: string;
  elements?: CollectionElement[];
  element_root?: string;
}

/** What a `collection` block is read off: a catalog field, or an element of a collection that is
 *  itself a collection — a party's `computed.injuries` on `claim.involved_parties` — which carries
 *  its own block in its own `hints`, exactly as a catalog field does. Only `hints` is read, so the
 *  readers below take either. */
export type CollectionBlockCarrier = Pick<CollectionElement, 'hints'>;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isCollectionHints = (value: unknown): value is CollectionHints =>
  isRecord(value) &&
  (isNil(value.rows) || typeof value.rows === 'string') &&
  (isNil(value.elements) || Array.isArray(value.elements)) &&
  (isNil(value.element_root) || typeof value.element_root === 'string');

const collectionBlock = (field: CollectionBlockCarrier): CollectionHints | undefined => {
  const block: unknown = field.hints?.[COLLECTION_HINT_KEY];

  return isCollectionHints(block) ? block : undefined;
};

/** `root`'s own fields in `catalog`, re-rooted onto one row: `<root>.x` is named `x` and loses the
 *  root's group segment, as the BE's `cel/collections.py::elements_from_root` re-roots them.
 *
 *  The BE moves every hint path onto the row as well; this keeps the hints as the catalog publishes
 *  them, because nothing in the editor reads a hint *path* — it reads whether a type guard is
 *  present, a range's bounds and a hierarchy's labels and values — and the compiler derives each
 *  element's own hints itself. */
const elementsFromRoot = (root: string, catalog: ReadonlyArray<FieldDefinitionDto>): CollectionElement[] => {
  const prefix = `${root}.`;

  return catalog
    .filter((field) => field.path.startsWith(prefix))
    .map((field) => ({
      name: field.path.slice(prefix.length),
      label: field.label,
      field_type: field.field_type,
      enum_values: field.enum_values,
      group: (field.group ?? []).slice(1),
      hints: field.hints ?? {},
    }));
};

/** The element declarations a collection field quantifies over, in declaration order.
 *
 *  The elements its block lists; else, for a block naming an `element_root` and listing none, that
 *  root's own fields in `catalog` re-rooted onto one row — the order the BE's
 *  `cel/collections.py::collection_element_fields` reads them in, so the body offers exactly what
 *  the compiler resolves. The engine ships a reference instead of a copy wherever the catalog
 *  carries the root, which is why `catalog` is required: without it such a body offers nothing.
 *
 *  Beside the hint key and the element types rather than beside either of its two callers: the
 *  nested builder derives its field list from this, and `validateConditionsTree` — a pure module
 *  the Formik `validate` prop calls on every keystroke — validates a match body against it, so
 *  reading it out of the sub-query chrome would pull every CMS Core field component in behind it.
 *
 *  Empty for a field carrying no block, which is a provider-side declaration bug rather than a
 *  shape to support: the body's field picker is then empty, and the BE reports "unknown element"
 *  on the first save. `hints` is an untyped dict on the wire, so the block is narrowed by a guard
 *  rather than asserted into shape. */
export const collectionElements = (
  field: CollectionBlockCarrier,
  catalog: ReadonlyArray<FieldDefinitionDto>
): CollectionElement[] => {
  const block = collectionBlock(field);
  if (isNil(block)) {
    return [];
  }
  if (!isNil(block.elements) && !isEmpty(block.elements)) {
    return block.elements;
  }
  return isNil(block.element_root) ? [] : elementsFromRoot(block.element_root, catalog);
};

/** What one row of a collection field is — `COLLECTION_ROWS_RECORDS` or `COLLECTION_ROWS_GROUPED` —
 *  read as grouped when its block does not say, as the BE's `collection_rows` reads it. */
export const collectionRows = (field: CollectionBlockCarrier): string => {
  const rows = collectionBlock(field)?.rows;

  return isNil(rows) || isEmpty(rows) ? COLLECTION_ROWS_GROUPED : rows;
};

export interface HierarchyChild {
  value: string;
  label: string;
}

export interface HierarchyValue {
  value: string;
  label: string;
  children: HierarchyChild[];
}

export interface HierarchyHints {
  parent_label: string;
  child_label: string;
  parent_path_segment: string;
  child_path_segment: string;
  values: HierarchyValue[];
  /** Per-parent child-label overrides. When a single parent is selected and
   *  its value appears here, the FE renders this label instead of the
   *  generic `child_label` fallback. Emitted by the BE
   *  `build_location_field_definitions` for the country/state composite
   *  (e.g. `{US: 'State', AU: 'State / Territory', GB: 'UK Country'}`). */
  child_label_overrides?: Record<string, string>;
  /** Whether this hierarchy has a second level at all. Distinct from "every
   *  parent's `children` is empty", which only says the *catalog* is empty and
   *  still admits free text — the BE emits `false` only when no config in scope
   *  defines a child (the legacy GL/WC incident config). Absent for hierarchies
   *  that don't declare it (e.g. the country/state composite), which the FE
   *  reads as "yes", preserving the two-level picker. */
  has_child_dimension?: boolean;
}

// The generated DTO's `field_type` is a string enum; override with our string union
// so callers can write `field_type: 'select'` directly without enum imports.
export type FieldDefinitionDto = Omit<FieldDefinitionListFieldDefinition, 'field_type'> & {
  field_type: FieldType;
};

/** Composite value shape for hierarchical fields (DR §5.13).
 *
 * `parent` is a single string for the standard "incident is X (maybe with sub
 * types)" leaf. It's a `string[]` for the multi-parent shape that legacy
 * `incident in (X, Y)` conversion produces — the compiler emits
 * `parent_path in [...]` and children MUST be empty in that case (different
 * parents may have different valid children). */
export interface HierarchicalValueShape {
  parent: string | string[];
  children: string[];
}

/** Point value shape for a date leaf's right-hand side: another instant, compared
 * against as it is — `Date of Loss is after Policy Expiration Date`.
 *
 * The alternative to a plain date string, on the same six comparison operators. The BE
 * dispatches on the presence of `anchor`, so that key is what makes a value anchored at
 * all, and on the *absence* of every offset key (`dir` / `n` / `unit`) that makes it a
 * point rather than an offset.
 *
 * `anchor` is `operators.ts::EVALUATED_AT_ANCHOR`, `TRIGGERED_AT_ANCHOR` or a
 * `date`/`datetime` catalog path. `precision` is the marker the editor derives from the two
 * operand types; absent means whole-day, which is what every value stored before the marker
 * existed means. */
export interface DatePointValueShape {
  anchor: string;
  precision?: DatePrecision;
}

/** Offset value shape for a date leaf's right-hand side:
 * `Claim Created at least 30 Days before When this rule runs`.
 *
 * `anchor` and `precision` as on `DatePointValueShape`. `dir` is one of `OFFSET_DIRECTIONS` —
 * direction is its own field, never the sign of `n`. `n` is a whole non-negative count; `''` is
 * the transient "author cleared the box" state, which `validateConditionsTree` blocks before it
 * can reach the BE compiler (which requires an int and 400s otherwise). `unit` is one of
 * `OFFSET_UNITS` — or of `EXACT_OFFSET_UNITS` on an exact comparison — and selects the CEL
 * function. Any one of `dir` / `n` / `unit` makes a value an offset, and the BE then requires
 * all three. */
export interface DateOffsetValueShape {
  anchor: string;
  dir: string;
  n: number | '';
  unit: string;
  precision?: DatePrecision;
}

/** Either anchored shape — what the BE's `operator_map.is_anchored_value` answers true for. */
export type AnchoredDateValueShape = DatePointValueShape | DateOffsetValueShape;

/** The quantifier a `collection` leaf carries in place of a comparison.
 *
 *  react-querybuilder's own `MatchConfig`, aliased rather than restated: a leaf of this shape has
 *  to be assignable to `RuleType`, which declares `match?: MatchConfig`, so a hand-written copy
 *  could only ever agree with it or be a bug. `mode` is the six-member `MatchMode` union.
 *  `threshold` is the number a count mode (`atLeast` / `atMost` / `exactly`) compares the matching
 *  rows against, and `null` while its box is empty. RQB writes it onto `match` whenever a field
 *  declaring match modes is selected, whatever the mode, and the other three modes never read it,
 *  so there it rides along on the wire unread — stripping it would mean fighting RQB's own
 *  field-change update every time, and `_compile_collection_leaf` reads it for a count mode only.
 *
 *  The *runtime* value is wider than the type: this is read back out of stored JSON, where a
 *  hand-edited configuration import can carry anything, which is why `isCollectionMatchLeaf`
 *  tests `typeof mode === 'string'` rather than membership and why the mode cell states a token
 *  it cannot offer rather than blanking. What reaches the BE is what `matchModes.ts` offers on the
 *  field: `rules_engine/cel/compiler.py::_MATCH_MODE_MACROS` on every collection, plus its
 *  `_MATCH_COUNT_MODES` on one whose rows are records. Anything else is a 400 on the next save. */
export type CollectionMatchConfig = MatchConfig;

/** A stored leaf that *quantifies over* a collection field.
 *
 *  Its `value` is not an operand but a nested react-querybuilder group whose leaves name the
 *  collection's own elements, and its `operator` means nothing — the BE's
 *  `_compile_collection_leaf` never reads it, because the quantifier lives in `match.mode`, and
 *  the editor writes none: react-querybuilder replaces the operator cell with the match-mode
 *  control for any field declaring `matchModes`. */
export type CollectionMatchLeaf = RuleType & { match: CollectionMatchConfig };

/**
 * Type guard: does this stored rule quantify over a collection, rather than compare a value?
 *
 * The same question the BE's `_compile_collection_leaf` asks first, on the same key — a `match`
 * block carrying a `mode`. The body's shape is deliberately *not* part of the answer: the BE
 * reports a non-group body as its own 400, and the read-only row renders whatever is stored, so
 * folding that check in here would only decide the same thing twice with less to show for it.
 *
 * @param rule - the rule react-querybuilder is rendering
 * @returns true when the rule carries a match block with a string `mode`
 */
export const isCollectionMatchLeaf = (rule: RuleType): rule is CollectionMatchLeaf => {
  // Through a widened view rather than off `rule.match` directly: v8 declares that slot as
  // `MatchConfig | undefined`, and what arrives here is stored JSON, which can carry a `match`
  // that is a string, a number, or a dict with no `mode` at all. Testing `typeof === 'string'`
  // is what keeps those out without asserting the stored token is one of the six.
  const { match } = rule as Partial<CollectionMatchLeaf>;

  return typeof match?.mode === 'string';
};
